/**
 * RegulationDetails Page
 * Main page for displaying regulation details with multiple sections
 */

import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import noDataAnim from "../assets/no-data-anim.webp";
import Fab from "@mui/material/Fab";
import Zoom from "@mui/material/Zoom";
import Tooltip from "@mui/material/Tooltip";
import { pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
  apiClient,
  type ApiResponse,
} from "../services/apiClient";
import {
  clearPersistedDomain,
  loadPersistedDomain,
  persistDomain,
} from "../utils/domainPersistence";
import { useFieldsConfig } from "../hooks";
import { useHasPrivilege } from "../hooks/useHasPrivilege";
import { useDepartmentAssessorTaskData } from "../hooks/useDepartmentAssessorTaskData";
import { useGlobalConfig } from "../hooks/useGlobalConfig";
import { useModuleDisplayName } from "../hooks/useModuleDisplayName";
import {
  REGULATION_ASSESSMENT_PACK_PRIVILEGES,
  REGULATION_AUTO_TAGGING_PRIVILEGES,
} from "../services/permissionsService";
import { MODULE_DISPLAY_NAME } from "../constants/moduleDisplayName";
import {
  submitTask,
  saveTask,
  getWorkflowTask,
  getConceptValues,
  listRegulationWorkflows,
  type WorkflowTaskDetail,
} from "../services/regulationsService";
import {
  applySavedTaskResultToTaskDetail,
  buildCustomPropertyMap,
  buildSaveTaskPayload,
  buildSubmitTaskPayload,
} from "../utils/taskDetailToFormConfig";
import { buildTaskTableSavePayloads } from "../utils/manualAssessmentTaskTable";
import type { ManualAssessmentFormContext } from "../components/RegulationDetails/ManualAssessmentForm";
import { postRegulationsNextHomeToParent } from "../utils/regulationsNextIframeHome";
import {
  buildRegulationDetailPath,
  buildRegulationDetailSearchPreserving,
  buildVersionedRegulationDetailPath,
  isOmitDomainNavSource,
  navigateToMyTask,
  parseDetailNavSearchParams,
  sanitizeDetailVersionParam,
} from "../utils/externalNavigation";
import { getManualAssessmentProcessPatHref } from "../utils/manualAssessmentProcessNameLink";
import {
  MANUAL_ASSESSMENT_SECTION_ID,
  SECTION_QUERY_PARAM,
  WHOLE_WORKFLOW_QUERY_PARAM,
  parseWholeWorkflowAssociationId,
} from "../utils/wholeWorkflowIframe";
import {
  regulationDetailsNavTabPanelSx,
  regulationDetailsNavTabsSx,
} from "../constants/regulationDetailsNavTabStyles";
import { setChatbotDetailSkipDomainForSession } from "../utils/chatbotDetailApiSession";
import { PDFViewer } from "../components/RegulationDetails/PDFViewer";
import { SummarySection } from "../components/RegulationDetails/SummarySection";
import { RegulationDetailsSummaryActions } from "../components/RegulationDetails/RegulationDetailsSummaryActions";
import { DetailsSection } from "../components/RegulationDetails/DetailsSection";
import { VersionComparisonSection } from "../components/RegulationDetails/VersionComparisonSection";
import { ImpactAssessmentSection } from "../components/RegulationDetails/ImpactAssessment";
import { TaggingImpactAssessmentSection } from "../components/RegulationDetails/TaggingImpactAssessment";
import { ManualAssessmentSection } from "../components/RegulationDetails/ManualAssessmentSection";
import { ManualAssessmentSubmitSuccess } from "../components/RegulationDetails/ManualAssessmentSubmitSuccess";
import ManualAssessmentCancelConfirmDialog from "../components/RegulationDetails/ManualAssessmentCancelConfirmDialog";
import { DetailsWorkflowSplitLayout } from "../components/RegulationDetails/DetailsWorkflowSplitLayout";
import { DepartmentAssessmentForm } from "../components/RegulationDetails/DepartmentAssessmentForm";
import type { AssessmentDocument } from "../types";
import {
  useAppStore,
  selectAssessmentSidebarIds,
  selectAssessmentSidebarSelectedIds,
  selectAssessmentSidebarAddedAt,
  selectAssessmentSidebarOpen,
  selectAssessmentSidebarCollapsed,
  selectAssessmentSidebarIdsCount,
  selectAssessmentSidebarVersionOverrides,
  selectUserRole,
} from "../stores";
import {
  AssessmentSidebar,
  SelectImpactAssessmentPlanDialog,
  useAutoAssessmentDialog,
} from "./HomePage/index";
import { useCompanyScopeImpactFlow } from "./HomePage/useCompanyScopeImpactFlow";
import {
  AIImpactAssessmentGuidanceMessage,
  AIImpactAssessmentResultsModal,
  CompanyScopeAlreadyAssessedDialog,
  isAiImpactGuidanceDismissed,
  StartManualAssessmentDialog,
  type AiResultsModalRow,
  type ManualAssessmentEntrySource,
} from "../components/AssessmentCenterPage";
import {
  buildConceptAssessmentResult,
  type TriageImpactedItem,
} from "../services/manualAssessmentService";
import { RunAiAssessmentMenuButton } from "../components/RegulationsTable/RunAiAssessmentMenuButton";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { useBrandThemeColors } from "../branding/context/useBrandThemeColors";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type SectionType =
  | "summary"
  | "details"
  | "versionComparison"
  | "taggingImpact"
  | "impactAssessment"
  | "manualAssessment"
  | "departmentAssessment";

function buildRegulationDetailEndpoint(
  paramId: string,
  urlVersion: string | null,
): string {
  if (urlVersion) {
    const versionNumber = urlVersion.startsWith("v")
      ? urlVersion.substring(1)
      : urlVersion;
    return `/regulations/${paramId}/version/${versionNumber}`;
  }
  return `/regulations/${paramId}`;
}

function formatVersionLabel(version: string | null | undefined): string {
  if (version == null || version === "") return "";
  return version.startsWith("v") ? version : `v${version}`;
}

/** Compare regulation versions ignoring an optional leading `v`. */
function regulationVersionsEqual(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  if (!a || !b) return false;
  const normalize = (value: string) =>
    value.startsWith("v") || value.startsWith("V") ? value.slice(1) : value;
  return normalize(a) === normalize(b);
}

function RegulationDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{
    id?: string;
    version?: string;
    workflowUuid?: string;
    taskId?: string;
  }>();
  const { id: paramId, version: pathVersionRaw, workflowUuid, taskId } = params;
  /** Task-entry mode: URL is /regulation-details/task/:workflowUuid/:taskId, read-only, data from workflow task API */
  const isTaskEntryMode = Boolean(workflowUuid && taskId);
  /** In task-entry mode, regulation id is resolved from associatedEntity.details (prefers regulation_id). */
  const [taskEntryRegulationId, setTaskEntryRegulationId] = useState<
    string | null
  >(null);
  const regulationId = isTaskEntryMode
    ? taskEntryRegulationId
    : (paramId ?? null);
  const [searchParams] = useSearchParams();
  const pathVersion = sanitizeDetailVersionParam(pathVersionRaw);
  const { version: queryVersion, source: detailSource } =
    parseDetailNavSearchParams(searchParams);
  /** Path version wins (email / Fusion modulePath); `?version=` remains for in-app jump links. */
  const urlVersion = pathVersion ?? queryVersion;
  /**
   * Jump links open `/regulation-details/:id?version=v1.0` and then strip
   * `version` from the URL so a later refresh is not pinned to that deep link.
   * Path-versioned `/regulation-details/:id/version/:version` keeps version in
   * the path so email refresh still lands on the same document.
   * Keep the originally requested version so that cleanup cannot race the
   * versioned fetch and load `/regulations/:id` (latest) instead.
   */
  const requestedDetailVersionRef = useRef<string | null>(urlVersion);
  const prevDetailParamIdRef = useRef(paramId);
  if (prevDetailParamIdRef.current !== paramId) {
    prevDetailParamIdRef.current = paramId;
    requestedDetailVersionRef.current = urlVersion;
  } else if (urlVersion) {
    requestedDetailVersionRef.current = urlVersion;
  }
  const detailFetchVersionKey = urlVersion ?? requestedDetailVersionRef.current;
  const urlDomain = searchParams.get("domain")?.trim() || null;
  const isOmitDomainDetailSource = isOmitDomainNavSource(detailSource);
  const wholeWorkflowAssociationId = parseWholeWorkflowAssociationId(
    searchParams.get(WHOLE_WORKFLOW_QUERY_PARAM),
  );
  const sectionFromQuery = searchParams.get(SECTION_QUERY_PARAM)?.trim() || "";

  const [selectedSection, setSelectedSection] =
    useState<SectionType>("details");
  const [regulationData, setRegulationData] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);
  /** 详情接口返回 status: 'failed' 时的后端提示，用于页面展示 */
  const [detailFetchError, setDetailFetchError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState<string>("");
  const [selectedVersion, setSelectedVersion] = useState("");
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [hasAssessmentHistory, setHasAssessmentHistory] = useState(false);
  const [hasManualAssessmentHistory, setHasManualAssessmentHistory] = useState(false);
  const [assessmentRefreshKey, setAssessmentRefreshKey] = useState(0);
  const [taskEntryTaskDetail, setTaskEntryTaskDetail] =
    useState<WorkflowTaskDetail | null>(null);
  /** Locks task form actions immediately after a successful save/submit (before refetch updates runningStatus). */
  const [taskEntryFormLocked, setTaskEntryFormLocked] = useState(false);
  const [wholeWorkflowUrl, setWholeWorkflowUrl] = useState<string | null>(() =>
    getManualAssessmentProcessPatHref(wholeWorkflowAssociationId ?? "") || null,
  );
  const [taskEntryDepartmentOptions, setTaskEntryDepartmentOptions] = useState<
    Array<{ code: string; name: string }>
  >([]);
  /** Stable ref so scroll re-renders (e.g. back-to-top) do not remount dynamic forms. */
  const taskEntryOptionsByField = useMemo(
    () => ({ department: taskEntryDepartmentOptions }),
    [taskEntryDepartmentOptions],
  );
  /** In task-entry, workflow list API expects regulation_id (e.g. INS163626); set from task details. */
  const [, setTaskEntryRegulationIdForWorkflows] = useState<string | null>(
    null,
  );

  const [startManualDialogOpen, setStartManualDialogOpen] = useState(false);
  const [startManualImpactedItems, setStartManualImpactedItems] = useState<
    TriageImpactedItem[] | undefined
  >();
  const [startManualConceptResult, setStartManualConceptResult] = useState<
    ReturnType<typeof buildConceptAssessmentResult> | null
  >(null);
  const [startManualEntrySource, setStartManualEntrySource] =
    useState<ManualAssessmentEntrySource>("direct");
  const [selectPlanDialogOpen, setSelectPlanDialogOpen] = useState(false);
  const [planDialogRunIdsOverride, setPlanDialogRunIdsOverride] = useState<
    string[] | null
  >(null);
  const [aiGuidanceOpen, setAiGuidanceOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "success" });
  /** After BPMN task submit success: show Figma success page instead of auto-close. */
  const [showTaskSubmitSuccess, setShowTaskSubmitSuccess] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  // Assessment sidebar (Selected for Assessment)
  const assessmentSidebarIds = useAppStore(selectAssessmentSidebarIds);
  const assessmentSidebarSelectedIds = useAppStore(
    selectAssessmentSidebarSelectedIds,
  );
  const assessmentSidebarOpen = useAppStore(selectAssessmentSidebarOpen);
  const assessmentSidebarCollapsed = useAppStore(
    selectAssessmentSidebarCollapsed,
  );
  const assessmentSidebarIdsCount = useAppStore(
    selectAssessmentSidebarIdsCount,
  );
  const assessmentSidebarAddedAt = useAppStore(selectAssessmentSidebarAddedAt);
  const userRole = useAppStore(selectUserRole);
  const toggleAssessmentSidebarCollapsed = useAppStore(
    (state) => state.toggleAssessmentSidebarCollapsed,
  );
  const removeAssessmentSidebarId = useAppStore(
    (state) => state.removeAssessmentSidebarId,
  );
  const toggleAssessmentSidebarSelectedId = useAppStore(
    (state) => state.toggleAssessmentSidebarSelectedId,
  );
  const clearAssessmentSidebarIds = useAppStore(
    (state) => state.clearAssessmentSidebarIds,
  );
  const assessmentSidebarVersionOverrides = useAppStore(
    selectAssessmentSidebarVersionOverrides,
  );
  const { config } = useGlobalConfig();
  const moduleDisplayName = useModuleDisplayName();
  const maxRegulationsPerBatch = config?.max_regulations_per_batch ?? 5;
  const { primaryColor } = useBrandThemeColors();

  const handleShowSnackbar = useCallback(
    (state: {
      open: boolean;
      message: string;
      severity: "success" | "error" | "info";
    }) => setSnackbar(state),
    [],
  );

  const versionStr =
    selectedVersion || (regulationData?.version as string)?.toString() || "";

  const regulationVersionForApi = useMemo(() => {
    const t = versionStr.trim();
    if (!t) return "";
    return t.startsWith("v") || t.startsWith("V") ? t.slice(1) : t;
  }, [versionStr]);

  /** Prefer regulation_id (INS…) over internal id for assessment APIs. */
  const regulationIdForAssessments = useMemo(() => {
    const rid = regulationData?.regulation_id;
    if (typeof rid === "string" && rid.trim()) return rid.trim();
    return regulationId ?? "";
  }, [regulationData?.regulation_id, regulationId]);

  const regulationTitleForManual =
    (regulationData?.title as string)?.trim() ||
    regulationId ||
    "Regulation";

  const handleOpenStartManualAssessmentDialog = useCallback(() => {
    if (!regulationId || !regulationVersionForApi) {
      handleShowSnackbar({
        open: true,
        message: "Regulation version is not ready yet. Please try again.",
        severity: "info",
      });
      return;
    }
    setStartManualImpactedItems(undefined);
    setStartManualConceptResult(null);
    setStartManualEntrySource("direct");
    setStartManualDialogOpen(true);
  }, [regulationId, regulationVersionForApi, handleShowSnackbar]);

  const handleStartManualAssessmentFromAiResultsRow = useCallback(
    (row: AiResultsModalRow) => {
      if (!regulationVersionForApi) {
        handleShowSnackbar({
          open: true,
          message: "Regulation version is not ready yet. Please try again.",
          severity: "info",
        });
        return;
      }
      const items =
        row.impactedItems && row.impactedItems.length > 0
          ? [...row.impactedItems]
          : undefined;
      setStartManualImpactedItems(items);
      const level = (row.impactLevel ?? "no").trim();
      setStartManualConceptResult(
        items
          ? buildConceptAssessmentResult(
              row.regulationId,
              row.version,
              level.toLowerCase() !== "no" && level !== "—",
              level,
              row.reportUrl,
              items,
            )
          : null,
      );
      setStartManualEntrySource("from_ai_impact");
      setStartManualDialogOpen(true);
    },
    [regulationVersionForApi, handleShowSnackbar],
  );
  const companyScopeRegulationId = useMemo(() => {
    if (regulationId) return regulationId;
    const rid = regulationData?.regulation_id;
    if (typeof rid === "string" && rid.trim()) return rid.trim();
    const dataId = regulationData?.id;
    if (typeof dataId === "string" && dataId.trim()) return dataId.trim();
    return null;
  }, [regulationId, regulationData]);

  const favoriteRegulationId = useMemo(() => {
    const rid = regulationData?.regulation_id;
    if (typeof rid === "string" && rid.trim()) return rid.trim();
    if (companyScopeRegulationId) return companyScopeRegulationId;
    const dataId = regulationData?.id;
    if (typeof dataId === "string" && dataId.trim()) return dataId.trim();
    return null;
  }, [regulationData, companyScopeRegulationId]);

  useEffect(() => {
    if (regulationData) {
      setIsFavorite(Boolean(regulationData.is_favorite));
    } else {
      setIsFavorite(false);
    }
  }, [regulationData]);

  const handleDetailFavoriteChange = useCallback((nextFavorite: boolean) => {
    setIsFavorite(nextFavorite);
    setRegulationData((prev) =>
      prev ? { ...prev, is_favorite: nextFavorite } : prev,
    );
  }, []);
  const versionOverrides =
    regulationId && versionStr
      ? {
          [regulationId]: versionStr.startsWith("v")
            ? versionStr
            : `v${versionStr}`,
        }
      : undefined;

  const mergedRegulationVersionOverrides = useMemo(
    () => ({
      ...assessmentSidebarVersionOverrides,
      ...(versionOverrides ?? {}),
    }),
    [assessmentSidebarVersionOverrides, versionOverrides],
  );

  const handleCloseSelectPlanDialog = useCallback(() => {
    setSelectPlanDialogOpen(false);
    setPlanDialogRunIdsOverride(null);
    setAssessmentRefreshKey((prev) => prev + 1);
  }, []);

  const handleOpenSelectPlanFromSidebar = useCallback(() => {
    setPlanDialogRunIdsOverride(null);
    setSelectPlanDialogOpen(true);
  }, []);

  const queryClient = useQueryClient();

  const refreshDetailAssessmentData = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["assessment-center"] });
    setAssessmentRefreshKey((prev) => prev + 1);
  }, [queryClient]);

  const cancelCompanyScopeRegulationPollRef = useRef<() => void>(() => {});

  const autoAssessmentDialog = useAutoAssessmentDialog({
    open: selectPlanDialogOpen,
    assessmentSidebarIds,
    regulationIdsForVersionLookup:
      planDialogRunIdsOverride && planDialogRunIdsOverride.length > 0
        ? planDialogRunIdsOverride
        : assessmentSidebarIds,
    runAssessmentIds:
      planDialogRunIdsOverride && planDialogRunIdsOverride.length > 0
        ? planDialogRunIdsOverride
        : assessmentSidebarSelectedIds.length > 0
          ? assessmentSidebarSelectedIds
          : undefined,
    onShowSnackbar: handleShowSnackbar,
    onCloseDialog: handleCloseSelectPlanDialog,
    regulationVersionOverrides: mergedRegulationVersionOverrides,
    onAiResultsModalClose: () => cancelCompanyScopeRegulationPollRef.current(),
    onCancelExternalResultsPoll: () =>
      cancelCompanyScopeRegulationPollRef.current(),
  });

  const companyScopeImpact = useCompanyScopeImpactFlow({
    selectedIds: companyScopeRegulationId ? [companyScopeRegulationId] : [],
    regulationVersionOverrides: mergedRegulationVersionOverrides,
    maxRegulationsPerBatch,
    onShowSnackbar: handleShowSnackbar,
    openAiResultsModalWithRows: autoAssessmentDialog.openAiResultsModalWithRows,
    updateAiResultsModalRows: autoAssessmentDialog.updateAiResultsModalRows,
    setAiResultsTableLoading: autoAssessmentDialog.setAiResultsTableLoading,
    refreshAssessmentCenter: refreshDetailAssessmentData,
  });

  cancelCompanyScopeRegulationPollRef.current =
    companyScopeImpact.cancelRegulationStatusPolling;

  const isDetailCompanyScopeStarting = useMemo(
    () =>
      companyScopeRegulationId != null &&
      companyScopeImpact.companyScopeLoadingRegulationIds.includes(
        companyScopeRegulationId,
      ),
    [
      companyScopeRegulationId,
      companyScopeImpact.companyScopeLoadingRegulationIds,
    ],
  );

  const handleCloseAiResultsModal = useCallback(() => {
    autoAssessmentDialog.handleCloseAiResultsModal();
    if (!isAiImpactGuidanceDismissed()) {
      setAiGuidanceOpen(true);
    }
  }, [autoAssessmentDialog]);

  const summaryRef = useRef<HTMLDivElement>(null);
  const detailsRef = useRef<HTMLDivElement>(null);
  const versionComparisonRef = useRef<HTMLDivElement>(null);
  const taggingImpactRef = useRef<HTMLDivElement>(null);
  const impactAssessmentRef = useRef<HTMLDivElement>(null);
  const manualAssessmentRef = useRef<HTMLDivElement>(null);
  const departmentAssessmentRef = useRef<HTMLDivElement>(null);

  // Flags to prevent scroll spy during programmatic scrolling
  const isScrollingProgrammatically = useRef(false);
  /** Task-entry deep link: auto-scroll to Manual Impact Assessment once per task. */
  const taskEntryAutoScrollDoneRef = useRef(false);

  // Use fields config hook
  const { fields: detailFields } = useFieldsConfig("detail");
  // List TAG columns (Country / Department / …) must also appear on detail.
  const { fields: listFields } = useFieldsConfig("list");

  // Privileges are loaded once globally by `PrivilegesBootstrap` in main.tsx.
  const canExecuteAutoImpact = useHasPrivilege("gri.assessment.impact.execute");
  const canRunSystemImpact = useHasPrivilege(
    "gri.assessment.impact.system.execute",
  );
  const canExecuteManualAssessment = useHasPrivilege(
    "gri.assessment.manual.execute",
  );
  const canReadManualAssessment = useHasPrivilege(
    "gri.assessment.manual.read",
  );

  const handleQueueLimitExceeded = useCallback(() => {
    handleShowSnackbar({
      open: true,
      message: `You can add up to ${maxRegulationsPerBatch} regulations to the assessment queue.`,
      severity: "info",
    });
  }, [handleShowSnackbar, maxRegulationsPerBatch]);
  const canReadComparison = useHasPrivilege("gri.assessment.comparison.read");
  const canReadImpact = useHasPrivilege("gri.assessment.impact.read");
  const canReadChecklist = useHasPrivilege("gri.assessment.checklist");
  const canReadAssessmentPack = useHasPrivilege(
    REGULATION_ASSESSMENT_PACK_PRIVILEGES.read,
  );
  const canReadRegulationAutoTagging = useHasPrivilege(
    REGULATION_AUTO_TAGGING_PRIVILEGES.read,
  );
  const canShowTaggingImpact =
    canReadAssessmentPack || canReadRegulationAutoTagging;

  // Normal path triage task fetching removed to optimize and hide Task Form in normal path

  const isDepartmentTaskRole =
    isTaskEntryMode &&
    (userRole === "department_assessor" || userRole === "department_approver");
  const deptAssessorTaskData = useDepartmentAssessorTaskData(
    isDepartmentTaskRole ? (regulationId ?? undefined) : undefined,
    isDepartmentTaskRole
      ? selectedVersion || (regulationData?.version as string) || undefined
      : undefined,
  );

  const showVersionComparison = useMemo(() => {
    const versions = (regulationData?.versions as Array<string | { version: string }>) || [];
    if (!canReadComparison || versions.length <= 1) return false;
    const normalized = versions
      .map((v) => (typeof v === "string" ? v : v.version))
      .sort((a, b) => {
        const aRaw = a.startsWith("v") ? a.substring(1) : a;
        const bRaw = b.startsWith("v") ? b.substring(1) : b;
        return parseFloat(aRaw) - parseFloat(bRaw);
      });
    const selectedRaw = selectedVersion.startsWith("v")
      ? selectedVersion.substring(1)
      : selectedVersion;
    const minRaw = normalized[0].startsWith("v")
      ? normalized[0].substring(1)
      : normalized[0];
    return minRaw !== selectedRaw;
  }, [canReadComparison, regulationData?.versions, selectedVersion]);

  /** Omits domain header while detail is open with ?source=chatbot|assessmentCenter|AIHub|…. */
  useEffect(() => {
    if (!isOmitDomainDetailSource) return;
    setChatbotDetailSkipDomainForSession(true);
    return () => setChatbotDetailSkipDomainForSession(false);
  }, [isOmitDomainDetailSource]);

  /**
   * Deep link ?domain= (e.g. global search) overrides HomePage/store domain for this page only.
   * useLayoutEffect runs before detail fetch so the first request uses the passed-in header.
   */
  useLayoutEffect(() => {
    if (!urlDomain || isOmitDomainDetailSource) return;
    const previous = loadPersistedDomain();
    persistDomain(urlDomain);
    return () => {
      if (previous) persistDomain(previous);
      else clearPersistedDomain();
    };
  }, [urlDomain, isOmitDomainDetailSource]);

  // Strip ?version= only after the matching document is on screen. A timed
  // strip used to retrigger fetch with urlVersion=null and load latest.
  // Path-versioned URLs keep `:version` so email / Fusion links stay pinned.
  useEffect(() => {
    if (!queryVersion || pathVersion) return;
    if (!regulationVersionsEqual(queryVersion, selectedVersion)) return;
    navigate(
      `${location.pathname}${buildRegulationDetailSearchPreserving(searchParams)}`,
      { replace: true },
    );
  }, [
    queryVersion,
    pathVersion,
    selectedVersion,
    location.pathname,
    navigate,
    searchParams,
  ]);

  useEffect(() => {
    taskEntryAutoScrollDoneRef.current = false;
  }, [workflowUuid, taskId]);

  // Handle initial hash navigation
  useEffect(() => {
    /**
     * Fusion / Shell integration:
     * When this page is opened via `?modulePath=...`, the shell usually keeps that param in the address bar.
     * We try to strip it from the top window immediately so that a manual browser REFRESH doesn't 
     * keep reloading the details page.
     */
    const stripParamsFromTop = () => {
      try {
        const topWin = window.top;
        if (topWin && topWin !== window.self) {
          // 1. Notify shell to clear state if cross-origin
          postRegulationsNextHomeToParent();

          // 2. Same-origin: try to modify address bar directly
          if (topWin.location.origin === window.location.origin) {
            const u = new URL(topWin.location.href);
            let changed = false;
            if (u.searchParams.has("modulePath")) {
              u.searchParams.delete("modulePath");
              changed = true;
            }
            if (u.searchParams.has("version")) {
              u.searchParams.delete("version");
              changed = true;
            }
            if (changed) {
              topWin.history.replaceState(topWin.history.state, "", u.pathname + u.search + u.hash);
            }
          }
        }
        
        // 3. Always clear this frame's URL too
        const selfUrl = new URL(window.location.href);
        if (selfUrl.searchParams.has("modulePath")) {
          selfUrl.searchParams.delete("modulePath");
          window.history.replaceState(window.history.state, "", selfUrl.pathname + selfUrl.search + selfUrl.hash);
        }
      } catch (e) {
        // Cross-origin: the postMessage above already handled the notification
      }
    };

    stripParamsFromTop();

    if (!loading && regulationData) {
      const hash = location.hash.replace("#", "");
      const validSections: SectionType[] = [
        "details",
        "versionComparison",
        "taggingImpact",
        "impactAssessment",
        "manualAssessment",
        "departmentAssessment",
      ];
      const requestedSection =
        (validSections.includes(hash as SectionType) ? hash : "") ||
        (validSections.includes(sectionFromQuery as SectionType)
          ? sectionFromQuery
          : "") ||
        (wholeWorkflowAssociationId ? MANUAL_ASSESSMENT_SECTION_ID : "");
      if (requestedSection) {
        setTimeout(() => {
          scrollToSection(requestedSection as SectionType, false);
        }, 500);
      } else if (
        isTaskEntryMode &&
        !taskEntryAutoScrollDoneRef.current
      ) {
        taskEntryAutoScrollDoneRef.current = true;
        setTimeout(() => {
          scrollToSection("manualAssessment", false);
        }, 500);
      }
    }
  }, [
    loading,
    regulationData,
    isTaskEntryMode,
    location.hash,
    sectionFromQuery,
    wholeWorkflowAssociationId,
  ]);

  // Function to get document presigned URL
  const getDocumentUrl = async (filePath: string): Promise<string> => {
    try {
      const response = await apiClient.post(
        "/regulations/documents/user-presigned-url",
        {
          file_path: filePath,
        },
      );

      if (response.status === "success" && response.data?.presigned_url) {
        return response.data.presigned_url;
      }
      throw new Error("Failed to get presigned URL");
    } catch (error) {
      console.error("Error fetching presigned URL:", error);
      throw error;
    }
  };

  // Handle document download
  const handleDocumentDownload = async (doc: AssessmentDocument) => {
    try {
      if (!doc.file_path) {
        console.error("Document file_path is missing");
        return;
      }

      const url = await getDocumentUrl(doc.file_path);

      // Strip query parameters if present
      let cleanPath = doc.file_path;
      if (cleanPath.includes('?')) {
        cleanPath = cleanPath.split('?')[0];
      }
      
      // We should prefer parsing the filename from the URL rather than doc.name if we want to extract the UUID correctly
      const rawNameFromPath = cleanPath.split("/").pop() || "";
      const rawName = rawNameFromPath || doc.name || "document.pdf";

      const uuidRe =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

      const getDownloadName = (name: string): string => {
        // 1) "<prefix>_bt-<uuid>[.<ext>]"
        const btMarker = "_bt-";
        const btIdx = name.lastIndexOf(btMarker);
        if (btIdx > 0) {
          const prefix = name.slice(0, btIdx);
          const tail = name.slice(btIdx + btMarker.length);
          const dotIdx = tail.lastIndexOf(".");
          const uuidPart = dotIdx >= 0 ? tail.slice(0, dotIdx) : tail;
          const ext = dotIdx >= 0 ? tail.slice(dotIdx) : "";
          if (uuidRe.test(uuidPart)) {
            return `${prefix}${ext}`;
          }
        }

        // 2) "<uuid>_<rest>"
        const underscoreIdx = name.indexOf("_");
        if (underscoreIdx > 0) {
          const maybeUuid = name.slice(0, underscoreIdx);
          const rest = name.slice(underscoreIdx + 1);
          if (uuidRe.test(maybeUuid) && rest) return rest;
        }

        // 3) Fallback: if prefix looks like an id, strip it
        if (underscoreIdx !== -1 && underscoreIdx < 40) {
          const prefix = name.substring(0, underscoreIdx);
          if (/^[a-zA-Z0-9-]+$/.test(prefix)) {
            return name.substring(underscoreIdx + 1);
          }
        }
        return name;
      };

      const downloadName = getDownloadName(rawName);
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = downloadName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch {
        const link = document.createElement("a");
        link.href = url;
        link.download = downloadName;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Error downloading document:", error);
    }
  };

  // Handle document click
  const handleDocumentClick = async (doc: AssessmentDocument) => {
    try {
      if (!doc.file_path) {
        console.error("Document file_path is missing");
        return;
      }

      const url = await getDocumentUrl(doc.file_path);
      setSelectedPdfUrl(url);
      setPdfDialogOpen(true);
    } catch (error) {
      console.error("Error opening document:", error);
    }
  };

  // Handle PDF view from assessment history
  const handleViewPDF = (pdfUrl: string) => {
    setSelectedPdfUrl(pdfUrl);
    setPdfDialogOpen(true);
  };

  // Fetch assessment history to determine if the section should be shown
  useEffect(() => {
    const checkAssessmentHistory = async () => {
      if (!canReadImpact || !regulationIdForAssessments || !selectedVersion) {
        setHasAssessmentHistory(false);
        return;
      }

      try {
        const versionNumber = selectedVersion.startsWith("v")
          ? selectedVersion.substring(1)
          : selectedVersion;
        const response = await apiClient.get(
          `/regulations/${regulationIdForAssessments}/${versionNumber}/assessments/history`,
        );

        if (
          response.status === "success" &&
          Array.isArray(response.data) &&
          response.data.length > 0
        ) {
          setHasAssessmentHistory(true);
        } else {
          setHasAssessmentHistory(false);
        }
      } catch (error) {
        console.error("Error checking assessment history:", error);
        setHasAssessmentHistory(false);
      }
    };

    checkAssessmentHistory();
  }, [
    regulationIdForAssessments,
    selectedVersion,
    canReadImpact,
    assessmentRefreshKey,
  ]);

  // Fetch manual assessment history to determine if the section should be shown
  useEffect(() => {
    const checkManualHistory = async () => {
      if (!regulationIdForAssessments || !selectedVersion) {
        setHasManualAssessmentHistory(false);
        return;
      }

      try {
        const res = await listRegulationWorkflows(
          regulationIdForAssessments,
          selectedVersion,
        );
        if (res.status === "success" && Array.isArray(res.data) && res.data.length > 0) {
          setHasManualAssessmentHistory(true);
        } else {
          setHasManualAssessmentHistory(false);
        }
      } catch (error) {
        console.error("Error checking manual assessment history:", error);
        setHasManualAssessmentHistory(false);
      }
    };

    checkManualHistory();
  }, [regulationIdForAssessments, selectedVersion, assessmentRefreshKey]);

  const isTaskEntryFormLocked =
    taskEntryFormLocked ||
    taskEntryTaskDetail?.task?.runningStatus === "Completed";

  // Task-entry mode: first GET workflow task, then use associatedEntity.details to fetch regulation
  useEffect(() => {
    setTaskEntryFormLocked(false);
    if (!isTaskEntryMode || !workflowUuid || !taskId) return;

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setDetailFetchError(null);
        const taskRes = await getWorkflowTask(workflowUuid, taskId);
        const data = taskRes.data as
          | {
              task?: unknown;
              associatedEntity?: {
                type?: string;
                details?: Record<string, unknown>;
              };
            }
          | null
          | undefined;
        setTaskEntryTaskDetail(
          taskRes.data != null
            ? (structuredClone(taskRes.data) as WorkflowTaskDetail)
            : null,
        );
        const details = data?.associatedEntity?.details;
        if (cancelled) return;
        if (!details || typeof details !== "object" || !("id" in details)) {
          setDetailFetchError(
            "Task response missing associated entity details",
          );
          setLoading(false);
          return;
        }
        const regId = String(details.id);
        const regulationIdForApi =
          details.regulation_id != null ? String(details.regulation_id) : regId;
        const v = details.version;
        const versionRaw =
          typeof v === "string" || typeof v === "number" ? String(v) : "1.0";
        const formattedVersion = versionRaw.startsWith("v")
          ? versionRaw
          : `v${versionRaw}`;
        setTaskEntryRegulationId(regulationIdForApi);
        setTaskEntryRegulationIdForWorkflows(regulationIdForApi);
        setSelectedVersion(formattedVersion);

        const versionNumber = versionRaw.startsWith("v")
          ? versionRaw.slice(1)
          : versionRaw;
        const regulationUrl = `/regulations/${regulationIdForApi}/version/${versionNumber}`;
        const result = await apiClient.get(regulationUrl);
        if (cancelled) return;
        if (result.status === "success" && result.data) {
          setRegulationData(result.data);
          setDetailFetchError(null);
        } else {
          // Fallback: use associatedEntity.details so Details/Summary can still render
          const fallbackData = { ...details } as Record<string, unknown>;
          const versionsList = Array.isArray(details.versions)
            ? details.versions
            : [versionRaw];
          fallbackData.versions = versionsList.map((ver: unknown) =>
            typeof ver === "string"
              ? ver.startsWith("v")
                ? ver
                : `v${ver}`
              : `v${versionRaw}`,
          );
          setRegulationData(fallbackData);
          setDetailFetchError(null);
        }
      } catch (error) {
        if (!cancelled) {
          setDetailFetchError(
            error instanceof Error
              ? error.message
              : "Failed to load task or regulation",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isTaskEntryMode, workflowUuid, taskId]);

  // Task-entry: fetch Department options for Manual Assessment form select fields
  useEffect(() => {
    if (!isTaskEntryMode) return;
    getConceptValues("Department")
      .then((res) => {
        const list = (res.data ?? []).map(
          (c: { code?: string; id?: string; name?: string }) => ({
            code: c.code ?? c.id ?? "",
            name: c.name ?? "",
          }),
        );
        setTaskEntryDepartmentOptions(list);
      })
      .catch(() => setTaskEntryDepartmentOptions([]));
  }, [isTaskEntryMode]);

  // Normal mode: fetch regulation by id (and optional version from URL)
  useEffect(() => {
    if (isTaskEntryMode || !paramId) return;
    let cancelled = false;
    const versionToFetch = requestedDetailVersionRef.current;

    const fetchRegulationDetails = async () => {
      const endpoint = buildRegulationDetailEndpoint(paramId, versionToFetch);

      const applyDetailSuccess = (data: Record<string, unknown>) => {
        setRegulationData(data);
        setDetailFetchError(null);
        const version = data.version as string;
        setSelectedVersion(formatVersionLabel(version));
      };

      const applyDetailFailure = (
        result: ApiResponse<unknown>,
        versionFallback: string,
      ) => {
        const msg = (result as { message?: string })?.message ?? null;
        const code = (result as { code?: number })?.code;
        setDetailFetchError(
          msg ||
            (code != null
              ? `Backend returned status: ${result?.status} (code: ${code})`
              : `Backend returned status: ${result?.status}`),
        );
        setSelectedVersion(formatVersionLabel(versionFallback));
      };

      try {
        setLoading(true);
        setDetailFetchError(null);

        const result = await apiClient.get(endpoint);
        if (cancelled) return;

        if (result.status === "success" && result.data) {
          applyDetailSuccess(result.data as Record<string, unknown>);
        } else {
          const versionToUse = versionToFetch || "1.0";
          applyDetailFailure(result, versionToUse);
        }
      } catch (error) {
        if (cancelled) return;
        console.error(
          "[RegulationDetails] Error fetching regulation details:",
          error,
        );
        setDetailFetchError(
          error instanceof Error
            ? error.message
            : "Failed to load regulation details",
        );
        const versionToUse = versionToFetch || "1.0";
        setSelectedVersion(formatVersionLabel(versionToUse));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchRegulationDetails();
    return () => {
      cancelled = true;
    };
  }, [isTaskEntryMode, paramId, detailFetchVersionKey]);

  // Handle version change (no-op in task-entry read-only mode)
  const handleVersionChange = async (version: string) => {
    if (isTaskEntryMode || !regulationId) return;

    if (pathVersion && paramId) {
      navigate(
        `${buildVersionedRegulationDetailPath(paramId, version)}${buildRegulationDetailSearchPreserving(searchParams)}`,
        { replace: true },
      );
      return;
    }

    try {
      setLoading(true);
      // Remove "v" prefix for API call if present
      const versionNumber = version.startsWith("v")
        ? version.substring(1)
        : version;
      const result = await apiClient.get(
        `/regulations/${regulationId}/version/${versionNumber}`,
      );

      if (result.status === "success" && result.data) {
        setRegulationData(result.data);
        setSelectedVersion(version);
        // Clear query version/hash from URL but keep domain (and other deep-link params)
        if (searchParams.has("version") || location.hash) {
          navigate(
            `${location.pathname}${buildRegulationDetailSearchPreserving(searchParams)}`,
            { replace: true },
          );
        }
        // Scroll to details section after version switch
        setTimeout(() => {
          scrollToTop()
        }, 100);
      }
    } catch (error) {
      console.error("Error fetching regulation version:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const contentContainer = document.querySelector(
        '[data-content-container="true"]',
      );

      // Show back to top button when scrolled down 300px
      if (contentContainer) {
        setShowBackToTop(contentContainer.scrollTop > 300);
      }

      if (isScrollingProgrammatically.current) {
        return;
      }

      const sections = [
        { id: "details" as SectionType, ref: detailsRef },
        ...(showVersionComparison
          ? [
              {
                id: "versionComparison" as SectionType,
                ref: versionComparisonRef,
              },
            ]
          : []),
        ...(canShowTaggingImpact
          ? [
              {
                id: "taggingImpact" as SectionType,
                ref: taggingImpactRef,
              },
            ]
          : []),
        ...(canReadImpact
          ? [
              {
                id: "impactAssessment" as SectionType,
                ref: impactAssessmentRef,
              },
            ]
          : []),
        ...(isTaskEntryMode ||
        (!isTaskEntryMode && canReadManualAssessment)
          ? [
              {
                id: "manualAssessment" as SectionType,
                ref: manualAssessmentRef,
              },
            ]
          : []),
        ...(isDepartmentTaskRole
          ? [
              {
                id: "departmentAssessment" as SectionType,
                ref: departmentAssessmentRef,
              },
            ]
          : []),
      ];

      if (!contentContainer) return;
      const containerTop = contentContainer.getBoundingClientRect().top;
      const stickyOffset = 120;
      let matched = false;
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.ref.current) {
          const relativeTop =
            section.ref.current.getBoundingClientRect().top - containerTop;
          if (relativeTop <= stickyOffset) {
            setSelectedSection(section.id);
            matched = true;
            break;
          }
        }
      }
      if (!matched) {
        setSelectedSection("details");
      }
    };

    const contentContainer = document.querySelector(
      '[data-content-container="true"]',
    );
    if (contentContainer) {
      contentContainer.addEventListener("scroll", handleScroll);
      return () => contentContainer.removeEventListener("scroll", handleScroll);
    }
  }, [
    showVersionComparison,
    canShowTaggingImpact,
    canReadImpact,
    regulationData,
    isTaskEntryMode,
    hasManualAssessmentHistory,
    canReadManualAssessment,
    isDepartmentTaskRole,
  ]);

  const scrollToSection = (sectionId: SectionType, isManualClick = true) => {
    let ref: React.RefObject<HTMLDivElement | null> | null = null;

    switch (sectionId) {
      case "summary":
        ref = summaryRef;
        break;
      case "details":
        ref = detailsRef;
        break;
      case "versionComparison":
        ref = versionComparisonRef;
        break;
      case "taggingImpact":
        ref = taggingImpactRef;
        break;
      case "impactAssessment":
        ref = impactAssessmentRef;
        break;
      case "manualAssessment":
        ref = manualAssessmentRef;
        break;
      case "departmentAssessment":
        ref = departmentAssessmentRef;
        break;
    }
    isScrollingProgrammatically.current = true;
    setSelectedSection(sectionId);

    // Clear URL parameters when manually navigating
    if (
      isManualClick &&
      (searchParams.toString() !== "" || location.hash !== "")
    ) {
      navigate(location.pathname, { replace: true });
    }

    const contentContainer = document.querySelector(
      '[data-content-container="true"]',
    );
    if (ref?.current && contentContainer) {
      const stickyOffset = 120;
      const containerRect = contentContainer.getBoundingClientRect();
      const elementRect = ref.current.getBoundingClientRect();
      const top =
        contentContainer.scrollTop +
        elementRect.top -
        containerRect.top -
        stickyOffset;

      contentContainer.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });

      let scrollEndTimer: ReturnType<typeof setTimeout>;
      const clearGuard = () => {
        isScrollingProgrammatically.current = false;
        clearTimeout(scrollEndTimer);
      };

      if ("onscrollend" in contentContainer) {
        contentContainer.addEventListener("scrollend", clearGuard, {
          once: true,
        });
        scrollEndTimer = setTimeout(clearGuard, 1500);
      } else {
        scrollEndTimer = setTimeout(clearGuard, 1500);
      }
    }
  };

  // Scroll to top function
  const scrollToTop = () => {
    const contentContainer = document.querySelector(
      '[data-content-container="true"]',
    );
    if (contentContainer) {
      contentContainer.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  /**
   * Return to Regulations Next home.
   * To clear `modulePath` from the browser address bar (Fusion shell), we must navigate the TOP window.
   * Even in cross-origin iframes, writing to `top.location.href` is generally allowed for navigation.
   * We avoid reading `top.location` to prevent security errors.
   */
  /**
   * Return to Regulations Next home.
   * If there are shell parameters (modulePath/version) in the URL, we perform a "hard" reset
   * by redirecting the top window. This is necessary to clear the shell's state.
   * If the URL is already clean, we perform a smooth SPA navigation.
   */
  const navigateToRegulationsNextHome = useCallback(() => {
    // Check whether any URL params exist
    const hasParams = (u: URL) => u.searchParams.toString().length > 0;
    
    let needsHardReset = false;
    try {
      // Check top frame (if same-origin)
      if (window.top && window.top !== window.self) {
        if (window.top.location.origin === window.location.origin) {
          if (hasParams(new URL(window.top.location.href))) {
            needsHardReset = true;
          }
        }
      }
    } catch (e) {
      // Cross-origin: In Fusion shell, we might be in an iframe.
      // We cannot directly read window.top.location due to cross-origin restrictions.
      
      // Fallback: check document.referrer which usually contains the parent's URL
      // This allows us to detect if the cross-origin parent container has parameters.
      if (document.referrer) {
        try {
          needsHardReset = hasParams(new URL(document.referrer));
        } catch (err) {
          // ignore invalid referrer
        }
      }
    }

    if (!needsHardReset) {
      // Smooth SPA navigation (when the parent container has no params)
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/", { replace: true });
      }
      return;
    }

    // Aggressive cleanup for shell integration (Full Page Reload)
    let targetUrl = "/regulationsnext";
    try {
      if (typeof document !== "undefined" && document.referrer) {
        const refUrl = new URL(document.referrer);
        targetUrl = `${refUrl.origin}${refUrl.pathname}`;
      }
    } catch (e) { /* fallback */ }

    try {
      if (window.top && window.top !== window.self) {
        window.open(targetUrl, "_top");
        return;
      }
    } catch (e) {
      postRegulationsNextHomeToParent();
    }

    window.location.href = targetUrl;
  }, [navigate, searchParams]);

  const handleGoBackToMyTask = useCallback(() => {
    navigateToMyTask();
  }, []);

  const handleTaskEntryCancelClick = useCallback(() => {
    setCancelConfirmOpen(true);
  }, []);

  const handleContinueEditingAfterCancel = useCallback(() => {
    setCancelConfirmOpen(false);
  }, []);

  const handleDiscardAndLeaveToMyTask = useCallback(() => {
    setCancelConfirmOpen(false);
    navigateToMyTask();
  }, []);

  const handleViewInHistory = useCallback(() => {
    const id =
      (typeof regulationData?.regulation_id === "string" &&
        regulationData.regulation_id.trim()) ||
      regulationId;
    if (!id) {
      handleShowSnackbar({
        open: true,
        message: "Regulation id missing. Cannot open history.",
        severity: "error",
      });
      return;
    }
    const version =
      selectedVersion ||
      (typeof regulationData?.version === "string"
        ? regulationData.version
        : undefined);
    const detailPath = buildRegulationDetailPath(id, {
      version: version || undefined,
    });
    const [pathname, search = ""] = detailPath.split("?");

    // Task-entry and normal detail share NewRegulationDetails at the same Outlet;
    // clear success UI before navigate or React may reuse state and keep showing it.
    setShowTaskSubmitSuccess(false);
    navigate(
      {
        pathname,
        search: search ? `?${search}` : "",
        hash: "#manualAssessment",
      },
      { replace: true },
    );
  }, [
    regulationData?.regulation_id,
    regulationData?.version,
    regulationId,
    selectedVersion,
    navigate,
    handleShowSnackbar,
  ]);

  // Safety: leaving task-entry must never keep the submit-success overlay.
  useEffect(() => {
    if (!isTaskEntryMode && showTaskSubmitSuccess) {
      setShowTaskSubmitSuccess(false);
    }
  }, [isTaskEntryMode, showTaskSubmitSuccess]);

  const handleOpenWholeWorkflow = useCallback((uri: string) => {
    setWholeWorkflowUrl(uri);
  }, []);

  const handleCloseWholeWorkflow = useCallback(() => {
    setWholeWorkflowUrl(null);
  }, []);

  const workflowIdentity = `${workflowUuid ?? ""}|${taskId ?? ""}|${paramId ?? ""}`;
  const prevWorkflowIdentityRef = useRef(workflowIdentity);
  useEffect(() => {
    if (prevWorkflowIdentityRef.current === workflowIdentity) return;
    prevWorkflowIdentityRef.current = workflowIdentity;
    setWholeWorkflowUrl(null);
  }, [workflowIdentity]);

  useEffect(() => {
    if (!wholeWorkflowAssociationId) return;
    const href = getManualAssessmentProcessPatHref(wholeWorkflowAssociationId);
    if (href) setWholeWorkflowUrl(href);
  }, [wholeWorkflowAssociationId]);

  useEffect(() => {
    if (showTaskSubmitSuccess) {
      setWholeWorkflowUrl(null);
    }
  }, [showTaskSubmitSuccess]);

  const renderMainContent = () => {
    if (showTaskSubmitSuccess) {
      return (
        <ManualAssessmentSubmitSuccess
          onGoBackToMyTask={handleGoBackToMyTask}
          onViewInHistory={handleViewInHistory}
        />
      );
    }
    const availableTabs = [
      { id: "details", label: "Details" },
    ];

    if (showVersionComparison) {
      availableTabs.push({
        id: "versionComparison",
        label: "Version Comparison",
      });
    }

    if (canShowTaggingImpact) {
      availableTabs.push({
        id: "taggingImpact",
        label: "Impact Assessment",
      });
    }

    if (canReadImpact) {
      availableTabs.push({
        id: "impactAssessment",
        label: "AI Impact Assessment",
      });
    }

    if (
      isTaskEntryMode ||
      (!isTaskEntryMode && canReadManualAssessment)
    ) {
      availableTabs.push({
        id: "manualAssessment",
        label: "Manual Impact Assessment",
      });
    }

    if (
      !isTaskEntryMode &&
      (userRole === "department_assessor" || userRole === "department_approver")
    ) {
      availableTabs.push({
        id: "departmentAssessment",
        label: "Department Assessment",
      });
    }

    return (
      <>
        <Box ref={summaryRef} id="summary-section">
          <SummarySection
            loading={loading}
            regulationData={regulationData}
            fields={detailFields}
            listFields={listFields}
            onDocumentClick={handleDocumentClick}
            onDocumentDownload={handleDocumentDownload}
            fetchErrorMessage={detailFetchError}
            title={regulationData?.title as string}
            favoriteRegulationId={
              !isTaskEntryMode ? favoriteRegulationId : null
            }
            isFavorite={isFavorite}
            onFavoriteChange={
              !isTaskEntryMode ? handleDetailFavoriteChange : undefined
            }
            actionButton={
              <RegulationDetailsSummaryActions
                regulationId={regulationId}
                versionLabel={versionStr}
                canExecuteAutoImpact={canExecuteAutoImpact}
                canReadChecklist={canReadChecklist}
                canExecuteManualAssessment={canExecuteManualAssessment}
                maxRegulationsPerBatch={maxRegulationsPerBatch}
                companyScopeStarting={isDetailCompanyScopeStarting}
                onCompanyScopeImpact={
                  canRunSystemImpact && companyScopeRegulationId
                    ? () => {
                        void companyScopeImpact.startCompanyScopeImpact([
                          companyScopeRegulationId,
                        ]);
                      }
                    : undefined
                }
                onCustomScopeImpact={
                  regulationId
                    ? () => {
                        setPlanDialogRunIdsOverride([regulationId]);
                        setSelectPlanDialogOpen(true);
                      }
                    : undefined
                }
                onStartManualAssessment={handleOpenStartManualAssessmentDialog}
                onQueueLimitExceeded={handleQueueLimitExceeded}
              />
            }
          />
        </Box>

        {/* Tabs for Navigation - Positioned above Details */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: "background.default",
            // borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Tabs
            value={selectedSection}
            onChange={(_, newValue) => scrollToSection(newValue)}
            variant="scrollable"
            scrollButtons={false}
            sx={regulationDetailsNavTabsSx}
          >
            {availableTabs.map((tab) => (
              <Tab key={tab.id} value={tab.id} label={tab.label} />
            ))}
          </Tabs>
        </Box>

        <Box
          sx={{
            ...regulationDetailsNavTabPanelSx,
            p: 0,
            "& > .section-wrapper": {
              p: 3,
              "&:not(:last-child)": {
                borderBottom: "1px solid #f0f0f0",
              },
            },
          }}
        >
          <Box
            ref={detailsRef}
            id="details-section"
            className="section-wrapper"
          >
            <DetailsSection
              loading={loading}
              regulationData={regulationData}
              fetchErrorMessage={detailFetchError}
            />
          </Box>
          {showVersionComparison && (
              <Box
                ref={versionComparisonRef}
                id="versionComparison-section"
                className="section-wrapper"
              >
                <VersionComparisonSection
                  loading={loading}
                  regulationData={regulationData}
                  currentVersion={selectedVersion}
                  onCompareVersions={() => {}}
                />
              </Box>
            )}
          {canShowTaggingImpact ? (
            <Box
              ref={taggingImpactRef}
              id="taggingImpact-section"
              className="section-wrapper"
            >
              <TaggingImpactAssessmentSection
                regulationId={regulationIdForAssessments}
                version={regulationVersionForApi}
              />
            </Box>
          ) : null}
          {canReadImpact && (
            <Box
              ref={impactAssessmentRef}
              id="impactAssessment-section"
              className="section-wrapper"
            >
              {aiGuidanceOpen ? (
                <Box sx={{ mb: 2 }}>
                  <AIImpactAssessmentGuidanceMessage
                    open={aiGuidanceOpen}
                    onClose={() => setAiGuidanceOpen(false)}
                  />
                </Box>
              ) : null}
              {hasAssessmentHistory ? (
                <ImpactAssessmentSection
                  onViewPDF={handleViewPDF}
                  regulationId={regulationIdForAssessments}
                  regulationVersion={
                    regulationVersionForApi ||
                    String(regulationData?.version ?? "").replace(/^v/i, "")
                  }
                  key={`impact-${assessmentRefreshKey}`}
                />
              ) : (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 2 }}>
                    AI Impact Assessment
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: 300,
                      // backgroundColor: "#f9fafb",
                      borderRadius: "8px",
                    }}
                  >
                    <Box
                      component="img"
                      src={noDataAnim}
                      alt="No data"
                      sx={{ width: 120, height: 120, mb: 2 }}
                    />
                    <Typography
                      variant="body2"
                      component="p"
                      sx={{ color: "#6b7280", textAlign: "center", m: 0 }}
                    >
                      No search results found.
                      {regulationId &&
                      (canRunSystemImpact ||
                        (canExecuteAutoImpact && canReadChecklist)) ? (
                        <>
                          {" "}
                          <RunAiAssessmentMenuButton
                            variant="link"
                            companyScopeStarting={isDetailCompanyScopeStarting}
                            onCompanyScopeImpact={
                              canRunSystemImpact && companyScopeRegulationId
                                ? () => {
                                    void companyScopeImpact.startCompanyScopeImpact(
                                      [companyScopeRegulationId],
                                    );
                                  }
                                : undefined
                            }
                            onCustomScopeImpact={
                              canExecuteAutoImpact && canReadChecklist
                                ? () => {
                                    setPlanDialogRunIdsOverride([regulationId]);
                                    setSelectPlanDialogOpen(true);
                                  }
                                : undefined
                            }
                          />
                        </>
                      ) : null}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
          {(isTaskEntryMode ||
            (!isTaskEntryMode && canReadManualAssessment)
            ) && (
            <Box
              ref={manualAssessmentRef}
              id="manualAssessment-section"
              className="section-wrapper"
            >
          {isTaskEntryMode ? (
            <ManualAssessmentSection
              showTaskForm={!isDepartmentTaskRole && taskEntryTaskDetail?.task?.runningStatus !== "Completed"}
              showDynamicForm={true}
              defaultTab={isDepartmentTaskRole || isTaskEntryFormLocked ? "history" : "taskForm"}
              regulationId={(regulationData?.regulation_id as string) || regulationId || ""}
              regulationVersion={(regulationData?.version as string) || ""}
              taskDetail={taskEntryTaskDetail}
              optionsByField={taskEntryOptionsByField}
              loading={loading}
              error={null}
              isLastStep={false}
              readOnly={isTaskEntryFormLocked}
              submitDisabled={isTaskEntryFormLocked}
              workflowId={workflowUuid ?? undefined}
              taskId={taskId ?? undefined}
              onReassignSuccess={(message) =>
                handleShowSnackbar({ open: true, message, severity: "success" })
              }
              onReassignError={(message) =>
                handleShowSnackbar({ open: true, message, severity: "error" })
              }
              onOpenWholeWorkflow={handleOpenWholeWorkflow}
              activeWholeWorkflowUrl={wholeWorkflowUrl}
              onReassigned={async () => {
                if (!workflowUuid || !taskId) return;
                const refetch = await getWorkflowTask(workflowUuid, String(taskId));
                if (refetch.data) setTaskEntryTaskDetail(refetch.data);
              }}
              onCancel={handleTaskEntryCancelClick}
                  onSave={async (formData, context?: ManualAssessmentFormContext) => {
                    const task = taskEntryTaskDetail?.task;
                    const processAssociationId =
                      task?.processAssociationId != null
                        ? String(task.processAssociationId)
                        : null;
                    const taskInstanceUniqueId =
                      task?.taskInstanceUniqueId != null
                        ? String(task.taskInstanceUniqueId)
                        : null;
                    const taskDefinitionId =
                      task?.taskDefinitionId != null
                        ? String(task.taskDefinitionId)
                        : null;
                    const tid = taskId ?? task?.taskId;
                    if (
                      !workflowUuid ||
                      !tid ||
                      !processAssociationId ||
                      !taskInstanceUniqueId ||
                      !taskDefinitionId
                    ) {
                      const message =
                        "Task or workflow missing. Cannot save draft.";
                      handleShowSnackbar({
                        open: true,
                        message,
                        severity: "error",
                      });
                      throw new Error(message);
                    }
                      try {
                      const tablePayloads = buildTaskTableSavePayloads(
                        context?.taskTables,
                        context?.taskTableState ?? {},
                      );
                      const savePayload = buildSaveTaskPayload(
                        taskEntryTaskDetail ?? null,
                        formData,
                        {
                          processAssociationId,
                          taskDefinitionId,
                          taskInstanceUniqueId,
                        },
                        tablePayloads.length === 1
                          ? tablePayloads[0]
                          : tablePayloads,
                        {
                          assignNextTask: context?.assignNextTask ?? undefined,
                          assignNextTaskFormData:
                            context?.assignNextTaskFormData,
                          taskTableDefinitions: context?.taskTables,
                          taskTableState: context?.taskTableState ?? {},
                        },
                      );
                      const res = await saveTask(
                        workflowUuid,
                        String(tid),
                        savePayload,
                      );
                      if (res.status !== "success") {
                        throw new Error(res.message ?? "Save failed");
                      }
                      try {
                        const refetch = await getWorkflowTask(
                          workflowUuid,
                          String(tid),
                        );
                        if (refetch.data) {
                          setTaskEntryTaskDetail(
                            applySavedTaskResultToTaskDetail(
                              structuredClone(refetch.data) as WorkflowTaskDetail,
                              formData.taskResult,
                            ),
                          );
                        }
                      } catch {
                        // Save already succeeded; keep local form state if refetch fails.
                      }
                      handleShowSnackbar({
                        open: true,
                        message: "Task form saved successfully.",
                        severity: "success",
                      });
                    } catch (err) {
                      handleShowSnackbar({
                        open: true,
                        message:
                          err instanceof Error ? err.message : "Save failed",
                        severity: "error",
                      });
                      throw err;
                    }
                  }}
                  onSubmit={async (formData, context?: ManualAssessmentFormContext) => {
                      const tid =
                        taskId ?? taskEntryTaskDetail?.task?.taskId;
                      const taskDefinitionId =
                        taskEntryTaskDetail?.task?.taskDefinitionId;
                      if (!tid || !taskDefinitionId || !workflowUuid) {
                        handleShowSnackbar({
                          open: true,
                          message: "Task or workflow missing. Cannot submit.",
                          severity: "error",
                        });
                        return;
                      }
                      const submitPayload = buildSubmitTaskPayload({
                        taskId: String(tid),
                        taskDefinitionId: String(taskDefinitionId),
                        taskDetail: taskEntryTaskDetail ?? null,
                        formData,
                        taskTables: context?.taskTables,
                        taskTableState: context?.taskTableState ?? {},
                        action: context?.action,
                        assignNextTask: context?.assignNextTask,
                        assignNextTaskFormData: context?.assignNextTaskFormData,
                        esignId: context?.esignId,
                      });
                      try {
                        const res = await submitTask(
                          workflowUuid,
                          String(tid),
                          submitPayload,
                        );
                        if (res.status === "success") {
                          setTaskEntryFormLocked(true);
                          setShowTaskSubmitSuccess(true);
                        } else {
                          handleShowSnackbar({
                            open: true,
                            message: res.message ?? "Submit failed",
                            severity: "error",
                          });
                        }
                      } catch (err) {
                        handleShowSnackbar({
                          open: true,
                          message:
                            err instanceof Error
                              ? err.message
                              : "Submit failed",
                          severity: "error",
                        });
                      }
                  }}
                />
              ) : (
                <ManualAssessmentSection
                  showTaskForm={false}
                  defaultTab="history"
                  regulationId={(regulationData?.regulation_id as string) || regulationId || ""}
                  regulationVersion={(regulationData?.version as string) || ""}
                  taskDetail={null}
                  optionsByField={{}}
                  loading={false}
                  error={null}
                  isLastStep={false}
                  submitDisabled={true}
                  readOnly={true}
                  onOpenWholeWorkflow={handleOpenWholeWorkflow}
                  activeWholeWorkflowUrl={wholeWorkflowUrl}
                  onCancel={() => scrollToSection("summary")}
                  onSubmit={() => {}}
                />
              )}
            </Box>
          )}
        </Box>

        {isDepartmentTaskRole && deptAssessorTaskData.taskDetail?.task?.runningStatus !== "Completed" && (
            <Box
              ref={departmentAssessmentRef}
              id="departmentAssessment-section"
            >
              <DepartmentAssessmentForm
                key={deptAssessorTaskData.taskDetail ? "loaded" : "initial"}
                department={deptAssessorTaskData.departmentDisplayName}
                assessor={deptAssessorTaskData.assessor}
                assessmentDate={deptAssessorTaskData.assessmentDate}
                scopeOptions={deptAssessorTaskData.marketOptions}
                loading={deptAssessorTaskData.loading}
                error={deptAssessorTaskData.error}
                submitDisabled={
                  deptAssessorTaskData.taskDetail?.task?.runningStatus ===
                  "Completed"
                }
                onCancel={
                  isTaskEntryMode
                    ? handleTaskEntryCancelClick
                    : () => scrollToSection("summary")
                }
                onSubmit={(values) => {
                  void (async () => {
                    const deptTaskId = deptAssessorTaskData.taskId;
                    const deptWorkflowId = deptAssessorTaskData.workflowId;
                    const taskDefinitionId =
                      deptAssessorTaskData.taskDefinitionId;
                    if (!deptWorkflowId || !deptTaskId || !taskDefinitionId) {
                      handleShowSnackbar({
                        open: true,
                        message:
                          "No active assessment task. Associate a workflow first.",
                        severity: "error",
                      });
                      return;
                    }
                    const taskResult =
                      values.impactFound === "yes"
                        ? "impact_found_yes"
                        : "impact_found_no";
                    const formDataDept: Record<string, unknown> = {
                      taskResult,
                      impact_found: values.impactFound,
                      rationale: values.rationale,
                      impact_priority: values.impactPriority,
                      scope_of_impact:
                        values.scopeOfImpact?.length > 0
                          ? values.scopeOfImpact
                          : undefined,
                      additional_comments: values.additionalComments,
                    };
                    const customPropertyMap = buildCustomPropertyMap(
                      deptAssessorTaskData.taskDetail,
                      formDataDept,
                    );
                    try {
                      const res = await submitTask(
                        deptWorkflowId,
                        deptTaskId,
                        {
                          taskId: deptTaskId,
                          taskDefinitionId,
                          taskResult,
                          customPropertyMap,
                        },
                      );
                      if (res.status === "success") {
                        const taskName =
                          deptAssessorTaskData.taskDetail?.task
                            ?.taskInstanceName ?? "Assessment";
                        handleShowSnackbar({
                          open: true,
                          message: `${taskName === "Assessment Approve" ? "Assessment" : taskName} submitted successfully.`,
                          severity: "success",
                        });
                        deptAssessorTaskData.refetch();
                      } else {
                        handleShowSnackbar({
                          open: true,
                          message: res.message ?? "Submit failed",
                          severity: "error",
                        });
                      }
                    } catch (err) {
                      handleShowSnackbar({
                        open: true,
                        message:
                          err instanceof Error ? err.message : "Submit failed",
                        severity: "error",
                      });
                    }
                  })();
                }}
              />
            </Box>
          )}
      </>
    );
  };

  const renderContent = () => {
    return (
      <Box
        sx={{
          transition: "margin-right 0.3s ease",
        }}
      >
        {renderMainContent()}
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <DetailsWorkflowSplitLayout
        workflowUri={wholeWorkflowUrl}
        onCloseWorkflow={handleCloseWholeWorkflow}
        backToTop={
          <Zoom in={showBackToTop}>
            <Tooltip title="Back to top" arrow placement="left">
              <Fab
                onClick={scrollToTop}
                size="medium"
                aria-label="scroll back to top"
                sx={{
                  position: "relative",
                  backgroundColor: "white",
                  "&:hover": {
                    backgroundColor: "#e9ebef",
                  },
                }}
              >
                <ArrowUpward />
              </Fab>
            </Tooltip>
          </Zoom>
        }
      >
      {/* Top Bar */}
      <Box sx={{ p: 3, pb: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          {/* Breadcrumbs Navigation */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Breadcrumbs
              separator="›"
              sx={{
                "& .MuiBreadcrumbs-separator": {
                  color: "#6b7280",
                  mx: 1,
                },
              }}
            >
              <Link
                component="button"
                variant="body1"
                onClick={navigateToRegulationsNextHome}
                sx={{
                  color: "#2563eb",
                  textDecoration: "none",
                  cursor: "pointer",
                  fontSize: (theme) => theme.typography.body2.fontSize,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {moduleDisplayName}
              </Link>

              <Typography
                sx={{
                  color: "text.primary",
                  fontSize: (theme) => theme.typography.body2.fontSize,
                  fontWeight: 500,
                }}
              >
                {String(
                  (regulationData as { regulation_id?: unknown } | null)
                    ?.regulation_id ?? regulationId ?? "",
                )}
              </Typography>
            </Breadcrumbs>

            {/* Version Selector (read-only in task-entry mode) */}
            <Select
              value={selectedVersion}
              onChange={(event: SelectChangeEvent) =>
                handleVersionChange(event.target.value)
              }
              displayEmpty
              size="small"
              disabled={isTaskEntryMode}
              renderValue={(value) => value}
              MenuProps={{
                slotProps: {
                  paper: {
                    sx: {
                      mt: 0.5,
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      minWidth: 200,
                      "& .MuiMenuItem-root": {
                        fontSize: "0.875rem",
                        py: 1.25,
                        px: 2,
                      },
                    },
                  },
                },
              }}
              sx={{
                fontSize: "0.875rem",
                minWidth: "80px",
                height: "28px",
                borderRadius: "4px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d1d5db",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#9ca3af",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#2563eb",
                  borderWidth: "1px",
                },
                "& .MuiSelect-select": {
                  py: 0.5,
                  px: 1.5,
                  color: "#374151",
                  fontWeight: 500,
                },
                "& .MuiSelect-icon": {
                  color: "#6b7280",
                },
              }}
            >
              {(() => {
                const baseVersions =
                  (regulationData?.versions as
                    | Array<string | { version: string }>
                    | null
                    | undefined) || [];
                const hasSelected = baseVersions.some((v) => {
                  const s =
                    typeof v === "string"
                      ? v
                      : (v as { version?: string })?.version;
                  const label = s?.startsWith("v") ? s : `v${s ?? ""}`;
                  return label === selectedVersion;
                });
                const versionsForSelect =
                  hasSelected || !selectedVersion
                    ? baseVersions
                    : [selectedVersion, ...baseVersions];
                return versionsForSelect.map((version) => {
                  const versionStr =
                    typeof version === "string"
                      ? version
                      : (version as any).version;
                  const versionLabel = versionStr?.startsWith("v")
                    ? versionStr
                    : `v${versionStr ?? ""}`;
                  return (
                    <MenuItem
                      key={versionLabel}
                      value={versionLabel}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Box component="span">{versionLabel}</Box>
                      {selectedVersion === versionLabel && (
                        <Box
                          component="span"
                          sx={{
                            color: "#9ca3af",
                            fontSize: "0.75rem",
                            fontWeight: 400,
                          }}
                        >
                          Current
                        </Box>
                      )}
                    </MenuItem>
                  );
                });
              })()}
            </Select>
          </Box>
        </Box>
      </Box>

      {/* Content Area with Sidebar */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          px: 3,
          pb: 3,
        }}
        data-content-container="true"
      >
        {renderContent()}
      </Box>
      </DetailsWorkflowSplitLayout>

      {/* PDF Preview Dialog */}
      <PDFViewer
        open={pdfDialogOpen}
        pdfUrl={selectedPdfUrl}
        onClose={() => setPdfDialogOpen(false)}
      />

      {/* Selected for Assessment Sidebar */}
      {assessmentSidebarOpen &&
        (canExecuteAutoImpact || canRunSystemImpact) && (
        <AssessmentSidebar
          assessmentSidebarIds={assessmentSidebarIds}
          assessmentSidebarSelectedIds={assessmentSidebarSelectedIds}
          assessmentSidebarCollapsed={assessmentSidebarCollapsed}
          assessmentSidebarIdsCount={assessmentSidebarIdsCount}
          regulationTitles={autoAssessmentDialog.regulationTitles}
          regulationVersions={autoAssessmentDialog.regulationVersions}
          regulationMarkets={autoAssessmentDialog.regulationMarkets}
          regulationProductTypes={autoAssessmentDialog.regulationProductTypes}
          regulationAddedAt={assessmentSidebarAddedAt}
          onToggleCollapsed={toggleAssessmentSidebarCollapsed}
          onRemoveId={removeAssessmentSidebarId}
          onClearAll={clearAssessmentSidebarIds}
          onToggleSelectedId={toggleAssessmentSidebarSelectedId}
          onCompanyScopeImpact={
            canRunSystemImpact &&
            assessmentSidebarSelectedIds.length > 0
              ? () => {
                  void companyScopeImpact.startCompanyScopeImpact(
                    assessmentSidebarSelectedIds,
                  );
                }
              : undefined
          }
          onCustomScopeImpact={
            canExecuteAutoImpact &&
            canReadChecklist &&
            assessmentSidebarSelectedIds.length > 0
              ? handleOpenSelectPlanFromSidebar
              : undefined
          }
          companyScopeStarting={isDetailCompanyScopeStarting}
        />
      )}

      {canReadChecklist && (
        <SelectImpactAssessmentPlanDialog
          open={selectPlanDialogOpen}
          onClose={handleCloseSelectPlanDialog}
          runLoading={autoAssessmentDialog.runAssessmentLoading}
          onRun={autoAssessmentDialog.handleRunAssessmentByPlan}
          runRegulationIds={
            planDialogRunIdsOverride && planDialogRunIdsOverride.length > 0
              ? planDialogRunIdsOverride
              : assessmentSidebarSelectedIds
          }
        />
      )}

      <CompanyScopeAlreadyAssessedDialog
        open={companyScopeImpact.partialDialogOpen}
        variant="partial"
        onClose={companyScopeImpact.closeCompanyScopeDialogs}
        alreadyCount={companyScopeImpact.pendingContext?.alreadyRun.length ?? 0}
        selectedTotal={companyScopeImpact.pendingContext?.selectedTotal ?? 0}
        alreadyRunRegulations={
          companyScopeImpact.pendingContext?.alreadyRun ?? []
        }
        onRunAnyway={companyScopeImpact.handlePartialRunAnyway}
        onSkipAlreadyRun={companyScopeImpact.handlePartialSkip}
        actionsDisabled={companyScopeImpact.companyScopeFlowLoading}
      />
      <CompanyScopeAlreadyAssessedDialog
        open={companyScopeImpact.allDialogOpen}
        variant="all"
        onClose={companyScopeImpact.closeCompanyScopeDialogs}
        alreadyCount={companyScopeImpact.pendingContext?.alreadyRun.length ?? 0}
        selectedTotal={companyScopeImpact.pendingContext?.selectedTotal ?? 0}
        alreadyRunRegulations={
          companyScopeImpact.pendingContext?.alreadyRun ?? []
        }
        onRunAnyway={companyScopeImpact.handleAllRunAnyway}
        actionsDisabled={companyScopeImpact.companyScopeFlowLoading}
      />

      <AIImpactAssessmentResultsModal
        open={autoAssessmentDialog.aiResultsModalOpen}
        rows={autoAssessmentDialog.aiResultsRows}
        loading={autoAssessmentDialog.aiResultsTableLoading}
        onClose={handleCloseAiResultsModal}
        onRetrySuccess={autoAssessmentDialog.resumeAiResultsPollAfterRerun}
        onOpenAssessmentCenter={() => navigate("/assessment-center")}
        onStartManualAssessment={
          canExecuteManualAssessment
            ? handleStartManualAssessmentFromAiResultsRow
            : undefined
        }
      />

      <StartManualAssessmentDialog
        open={startManualDialogOpen}
        regulation={{
          regulationId: regulationId ?? "",
          regulationVersion: regulationVersionForApi,
          regulationTitle: regulationTitleForManual,
        }}
        entrySource={startManualEntrySource}
        impactedItems={startManualImpactedItems}
        conceptAssessmentResult={startManualConceptResult}
        onClose={() => {
          setStartManualDialogOpen(false);
          setStartManualImpactedItems(undefined);
          setStartManualConceptResult(null);
          setStartManualEntrySource("direct");
        }}
        onInitiated={() => {
          setAssessmentRefreshKey((prev) => prev + 1);
        }}
      />

      <ManualAssessmentCancelConfirmDialog
        open={cancelConfirmOpen}
        onContinueEditing={handleContinueEditingAfterCancel}
        onDiscardAndLeave={handleDiscardAndLeaveToMyTask}
      />

      {/* Snackbar for Auto Assessment */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
}

export default RegulationDetails;
